import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, Target, Zap, Rocket, Users, Mail } from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';

export const CareersPage: React.FC = () => {
  usePageTitle('Careers at QuantumHire');
  const openRoles: any[] = []; // Empty for now to show the fallback

  return (
    <div className="min-h-screen bg-white pt-28 sm:pt-40 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-24"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-accent/10 text-brand-primary text-[10px] font-black uppercase tracking-[0.2em] mb-8 border border-brand-accent/20">
            Join the Mission
          </span>
          <h1 className="text-5xl md:text-8xl font-display font-bold text-brand-primary mb-8 tracking-tight leading-[0.95]">
            Build the Future <br />of Hiring
          </h1>
          <p className="text-xl md:text-2xl text-brand-muted font-medium max-w-3xl mx-auto italic">
            Join us in redefining how companies hire — combining technology, execution, and outcomes.
          </p>
        </motion.div>

        {/* Why Join */}
        <section className="mb-32">
          <h2 className="text-11px font-black uppercase tracking-[0.25em] text-brand-accent mb-12 text-center">Why Join QuantumHire</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Real Impact", desc: "Work on real-world hiring problems that affect millions.", icon: <Target className="w-5 h-5" /> },
              { title: "Category Defining", desc: "Build a product that sets a new standard for a legacy industry.", icon: <Rocket className="w-5 h-5" /> },
              { title: "High Ownership", desc: "Small teams, big responsibilities, and zero bureaucracy.", icon: <Zap className="w-5 h-5" /> },
              { title: "Execution Speed", desc: "Fast-paced environment focused on shipping and learning.", icon: <Sparkles className="w-5 h-5" /> }
            ].map((feature, idx) => (
              <div key={idx} className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 hover:border-brand-primary transition-all group">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-brand-primary group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-brand-primary mb-3">{feature.title}</h3>
                <p className="text-sm text-brand-muted font-medium leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Open Roles */}
        <section className="mb-32">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl font-display font-bold text-brand-primary">Open Roles</h2>
            <div className="hidden sm:block h-px flex-1 mx-8 bg-gray-100 mb-2" />
          </div>
          
          <div className="bg-gray-50 rounded-[3rem] p-12 text-center border border-dashed border-gray-200">
            {openRoles.length > 0 ? (
              <div className="space-y-4">
                {/* Role mapping logic here if needed later */}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm">
                  <Users className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-xl font-bold text-brand-primary">No open roles currently.</p>
                <p className="text-brand-muted font-medium max-w-sm mx-auto">
                  We're always looking for exceptional talent. Reach out to us to explore future opportunities.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* How We Work */}
        <section className="mb-32 grid md:grid-cols-3 gap-12">
          <div className="md:col-span-1">
             <h2 className="text-4xl font-display font-bold text-brand-primary mb-4">How We Work</h2>
             <p className="text-brand-muted font-medium">Our DNA is built on three core pillars that guide every decision.</p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-3 gap-8">
            {[
              { label: "Outcome-driven", desc: "No vanity metrics. We focus on successful hires." },
              { label: "Bias for Action", desc: "Better to ship and iterate than to over-plan." },
              { label: "Customer-first", desc: "We win when our employers and candidates win." }
            ].map((p, i) => (
              <div key={i} className="space-y-3">
                <div className="text-brand-accent font-black text-xs uppercase tracking-widest">{p.label}</div>
                <p className="text-sm text-brand-muted font-medium leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="p-16 bg-brand-primary rounded-[3rem] text-center text-white shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-display font-bold mb-8">Ready to join?</h2>
            <p className="text-xl text-brand-secondary mb-12 font-medium opacity-80 italic">
              Apply to an open role or reach out to us directly.
            </p>
            <div className="text-brand-accent text-3xl md:text-4xl font-display font-bold">
              <a href="mailto:info@quantumhire.ai" className="hover:underline transition-all">
                info@quantumhire.ai
              </a>
            </div>
          </div>
          <Mail className="absolute bottom-[-10%] right-[-10%] w-64 h-64 text-white opacity-5 rotate-12" />
        </section>
      </div>
    </div>
  );
};
